<template>
  <v-row no-gutters>
    <v-col class="pt-0">
      <sidebar-row v-if="!hideDivider && persistOldExtras">
        <v-divider></v-divider>
      </sidebar-row>
      <sidebar-row style="z-index: 0; position: relative;">
        <v-tabs
          v-model="currentTab"
          ref="tabs"
          :show-arrows="false"
          @change="() => $nextTick(() => manipulateTabs())"
          v-bind="computedTabsProps"
        >
          <v-tooltip
            bottom
            color="font"
            v-for="(tab, idx) in computedTabs"
            :key="tab.value"
            :disabled="tab.isTooltipDisabled"
          >
            <template #activator="{ on }">
              <div
                v-on="on"
                class="d-flex"
                style="height: auto"
                :class="[computedTabs.length > 3 || areTabsCompact ? 'extra-div__stretched' : 'extra-div']"
              >
                <v-tab :disabled="tab.isTabDisabled" v-bind="computedTabProps">
                  <!-- The following span is duplicated to ensure that the tabs remain the same width, preventing any jumping effect when switching between tabs. -->
                  <v-badge
                    :value="!!tab.amount"
                    :content="tab.amount"
                    :offset-y="8"
                    :offset-x="3"
                    overlap
                    bordered
                    style="z-index: 1"
                  >
                    <span
                      v-for="(_, index) in Array(2).fill(null)"
                      :key="index"
                      class="d-flex justify-center align-center"
                      style="gap: 0.3rem"
                      :style="
                        index === 0
                          ? 'margin: auto 0; height: 100%; position: absolute; width: 100%'
                          : 'visibility: hidden'
                      "
                    >
                      <v-icon>
                        {{ tab.icon }}
                      </v-icon>
                      <span v-if="!areTabsCompact && currentTab === idx">{{
                        index === 0 ? tab.text : longestTabWord
                      }}</span>
                    </span>
                  </v-badge>
                </v-tab>
              </div>
            </template>
            {{ tab.tooltipText }}
          </v-tooltip>
        </v-tabs>
      </sidebar-row>
      <v-row no-gutters>
        <v-col class="pr-3">
          <v-tabs-items v-model="currentTab">
            <v-tab-item v-for="tab in computedTabs" :key="tab.value">
              <slot :name="tab.value">
                <component
                  :is="tab.component"
                  :dataSourceName="dataSourceName"
                  :subDataSourceName="subDataSourceName"
                  :featureId="featureId"
                  :subFeatureId="subFeatureId"
                  :init-function="tab.initFunction"
                  v-bind="tab.additionalProps"
                />
              </slot>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { call, get } from 'vuex-pathify';
import Notes from '@/components/Notes';
import ChangeHistory from '@/components/ChangeHistory';
import Attachments from '@/components/Attachments';
import SidebarRow from '@/components/SidebarRow';
import Relations from '@/components/Relations';
import Suggestions from '@/components/Suggestions';

let registeredBodyWidth = 0;

export default {
  name: 'ObjectExtras',
  components: { SidebarRow, Attachments, ChangeHistory, Notes, Relations, Suggestions },
  mixins: [fetchAttributesSchemaMixin, prepareAttributesFeatureCardMixin],
  props: {
    layerId: {
      type: [String, Number],
      required: true,
    },
    featureId: {
      type: [String, Number],
      required: true,
    },
    subFeatureId: {
      type: [String, Number],
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    subDataSourceName: {
      type: String,
    },
    permissions: {
      type: Number,
      required: false,
    },
    areExtrasHidden: {
      type: Boolean,
      default: false,
    },
    hideDivider: {
      type: Boolean,
      default: false,
    },
    featureProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
    persistOldExtras: {
      type: Boolean,
      default: false,
    },
    tabs: {
      type: Array,
      default: () => {
        return ['changeHistory', 'notes', 'attachments', 'relations', 'suggestions'];
      },
      validator: value => {
        return value.every(v => ['changeHistory', 'notes', 'attachments', 'relations', 'suggestions'].includes(v));
      },
    },
    geometry: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    attachmentsFormSchema: [],
    currentTab: 0,
    relationsAttachmentsFetched: false,
    areTabsCompact: false,
  }),
  watch: {
    isNew(nV) {
      if (nV) this.currentTab = 0;
    },
  },
  computed: {
    additionalModules: get('admin/additionalModules'),
    settings: get('admin/settingsValues'),
    toolsPermissions: get('users/toolsPermissions'),
    attachmentsAttributesMetadata: get('layers/metadata@:documentationDataSourceName.attributes_schema.attributes'),
    attachmentsLayerFormSchema: get('layers/layers@:documentationLayerId@form_schema.elements'),
    documentationDataSourceName: get('admin/modulesMapping@documentation.attachment_datasource_name'),
    documentationLayerId: get('admin/modulesMapping@documentation.documentation_layer_id'),
    isSuggestionsModuleEnabled: get('admin/additionalModules@UPDATE_SUGGESTIONS_MODULE.enabled'),

    activeDataSourceName() {
      return this.subDataSourceName || this.dataSourceName;
    },
    activeFeatureId() {
      return this.subFeatureId || this.subFeatureId === 0 ? this.subFeatureId : this.featureId;
    },

    hasRelations() {
      return this.tabs.includes('relations');
    },
    relations: get('relations/relations'),
    relationsList() {
      return this.relations?.[this.dataSourceName]?.[this.featureId] || [];
    },

    allTabs() {
      const sortedTabs = this.persistOldExtras
        ? ['changeHistory', 'notes', 'attachments', 'relations', 'suggestions']
        : ['notes', 'attachments', 'relations', 'changeHistory', 'suggestions'];
      return [...(!this.persistOldExtras ? ['attributes'] : []), ...sortedTabs.filter(tab => this.tabs.includes(tab))];
    },
    computedTabsProps() {
      return !this.persistOldExtras
        ? {
            class: 'pb-4 pt-4',
            hideSlider: false,
          }
        : {
            hideSlider: true,
            class: `custom-tabs pb-4 pt-4 ${this.hideDivider ? 'pt-0' : ''}`,
          };
    },
    computedTabProps() {
      return !this.persistOldExtras
        ? {}
        : {
            class: 'classic_tab',
            activeClass: 'activeTab',
          };
    },
    longestTabWord() {
      return this.computedTabs.reduce((a, b) => (a.text.length > b.text.length ? a : b)).text;
    },
    computedTabs() {
      return this.computedValues
        .filter(item => (this.areExtrasHidden ? item.value === 'attributes' : true))
        .map((item, index) => {
          return {
            ...item,
            isTooltipDisabled: this.areTabsCompact ? false : this.currentTab === index,
            tooltipText: this.tooltipText(item.value, item.text),
            ...(item.value !== 'attributes' && {
              isTabDisabled: this.isDisabled(item.value),
            }),
          };
        });
    },
    computedValues() {
      const dict = {
        attributes: {
          text: this.$i18n.t('sidebar.attributes'),
          value: 'attributes',
          icon: 'mdi-list-box',
        },
        attachments: {
          text: this.$i18n.t('sidebar.attachments'),
          icon: 'mdi-paperclip',
          value: 'attachments',
          component: 'Attachments',
          initFunction: this.getRelationsWithAttachments,
          amount: this.featureProps[Object.keys(this.featureProps).find(key => key.includes('attachments_count'))],
          additionalProps: {
            formSchema: this.attachmentsFormSchema,
            expanded: this.hasExpandedAttachments,
            layerId: this.layerId,
          },
        },
        changeHistory: {
          text: this.$i18n.t('sidebar.changeHistory'),
          icon: 'mdi-lightning-bolt',
          value: 'changeHistory',
          component: 'ChangeHistory',
          amount: this.featureProps[Object.keys(this.featureProps).find(key => key.includes('history_count'))],
          additionalProps: {
            layerId: this.layerId,
          },
        },
        notes: {
          text: this.$i18n.t('sidebar.notes'),
          icon: 'mdi-comment-text',
          value: 'notes',
          component: 'Notes',
          amount: this.featureProps[Object.keys(this.featureProps).find(key => key.includes('notes_count'))],
          additionalProps: {
            layerId: this.layerId,
            isExtended: !this.persistOldExtras,
          },
        },
        relations: {
          text: this.$i18n.t('sidebar.relations'),
          icon: 'mdi-transit-connection-horizontal',
          value: 'relations',
          component: 'Relations',
          amount: this.relationsList?.length,
          initFunction: this.getRelationsWithAttachments,
          additionalProps: {
            descAttributeValue: this.featureProps.name,
          },
        },
        ...(this.isSuggestionsModuleVisible
          ? {
              suggestions: {
                text: this.$i18n.t('sidebar.suggestions'),
                icon: 'mdi-chat-alert',
                value: 'suggestions',
                component: 'Suggestions',
                additionalProps: {
                  geometry: this.geometry,
                },
              },
            }
          : {}),
      };
      return this.allTabs.map(tab => dict[tab]).filter(tab => !!tab);
    },
    isSuggestionsModuleVisible() {
      return (
        this.isSuggestionsModuleEnabled &&
        !!this.metadata[this.dataSourceName].geometry_type &&
        (!this.subDataSourceName || this.subDataSourceName === this.dataSourceName)
      );
    },
    hasExpandedAttachments() {
      const isModuleEnabled = this.additionalModules['DOCUMENTATION']?.enabled;
      const isSettingEnabled = this.settings['documentation_module_enabled'];
      const hasPermission = this.toolsPermissions['DOCUMENTATION']?.main_value > 0;
      return isModuleEnabled && isSettingEnabled && hasPermission;
    },
  },
  methods: {
    getRelations: call('relations/getRelations'),
    async getRelationsWithAttachments(payload, init = false) {
      if (this.relationsAttachmentsFetched && init) {
        return;
      }
      await this.getRelations(payload);
      if (this.hasExpandedAttachments) {
        this.attachmentsFormSchema = this.$_flattenStructure(
          this.getForm(this.attachmentsLayerFormSchema, this.attachmentsAttributesMetadata),
          'elements'
        );
      }
      this.relationsAttachmentsFetched = true;
    },
    setCurrentTab(tab) {
      // TODO: Fix po zmianach w przyciskach pod tytułami kart
      if (tab === 'history') {
        tab = 'changeHistory';
      }
      this.currentTab = this.allTabs.indexOf(tab);
    },
    isDisabled(value) {
      if (value === 'changeHistory') {
        return false;
      }
      return this.permissions === 0;
    },
    tooltipText(name, text) {
      return this.isDisabled(name) ? this.$i18n.t(`sidebar.noPermission${this.$_capitalize(name)}`) : text;
    },
    manipulateTabs() {
      /** This method can be used with ResizeObserver if there is a such need */
      const tabs = this.$refs.tabs;
      const wrapper = tabs?.$el.querySelector('.v-slide-group__wrapper');
      if (!wrapper) return;
      const currentBodyWidth = document.body.clientWidth;
      const tabsShouldBeCompact = wrapper.clientWidth + 2 < wrapper.scrollWidth;
      if (
        tabsShouldBeCompact !== this.areTabsCompact &&
        (registeredBodyWidth !== currentBodyWidth ? true : tabsShouldBeCompact)
      ) {
        this.areTabsCompact = tabsShouldBeCompact;
        tabs.callSlider();
      }
      registeredBodyWidth = currentBodyWidth;
    },
  },
  mounted() {
    registeredBodyWidth = 0;
    this.$nextTick(() => this.manipulateTabs());
    this.$watch(
      vm => {
        return {
          featureId: vm.activeFeatureId,
          dataSourceName: vm.activeDataSourceName,
          hasRelations: vm.hasRelations,
        };
      },
      nV => {
        const { featureId, dataSourceName, hasRelations } = nV;
        if (hasRelations && dataSourceName && featureId !== 'new' && (featureId || featureId === 0)) {
          this.getRelationsWithAttachments({ data_source_name: dataSourceName, feature_id: featureId });
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
  },
};
</script>

<style lang="scss" scoped>
.extra-div {
  flex-grow: 0;
  ::v-deep {
    .v-tab {
      padding: 0 1.7rem;
    }
    .v-slide-group__content {
      gap: 0.5rem;
    }
  }
}

.extra-div__stretched {
  flex-grow: 1;
  min-width: 45px;
  ::v-deep {
    .v-tab {
      padding: 0 0.5rem;
    }
    .v-slide-group__content {
      gap: 0;
    }
  }
}

.classic_tab {
  letter-spacing: 0.0178571429em !important;
  border-radius: 4px;
  padding: 8px !important;
  font-size: 12px;
  font-weight: 500;
  height: fit-content;
  text-transform: unset !important;
}
.classic_tab:not(.activeTab) {
  color: #5f6368 !important;
}
.activeTab {
  background-color: map-get($colors, 'primarybackground');
}
.custom-tabs {
  padding-top: 26px;
  position: sticky;
  z-index: 1;
  ::v-deep {
    .v-item-group {
      height: auto !important;
    }
  }
}
::v-deep {
  .v-tab {
    min-width: 100% !important;
  }
  .v-window {
    overflow: initial !important;
  }
  .v-slide-group__prev,
  .v-slide-group__next {
    display: none !important;
  }
}
</style>
